import { Component, OnInit, ViewChild, HostListener, ElementRef, ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MenuInfoLinks, MenuNode, MenuParentNode } from './menu-nav-bar.model';
import { MENIES } from './menies.constant';
import { TranslationService } from '../../shared-module/common/services/translation.service';
import { MenuService } from './menu.service';
import { TenantSettings } from '../../shared-module/common/services/tenant-settings.service';
import { CurrentUser } from '../../shared-module/common/services/current-user.service';
import { ILookup } from '../../shared-module/common/interfaces/ILookup.interface';
import { CpqAddNewChangeSetDialogComponent } from '../../shared-module/dialogs/cpq-add-new-change-set-dialog/cpq-add-new-change-set-dialog.component';
import { ChangeSet } from '../../shared-module/common/models/ChangeSet.model';
import { ChangeSetService } from '../../shared-module/common/services/change-set.service';
import { CpqSelectComponent, IconType, SelectType } from '../../shared-module/cpq-select/cpq-select.component';
import { CreateNewItemOptions } from '../../shared-module/cpq-select/cpq-select.model';
import { any } from '../../shared-module/common/functions/js-linq';
import { AdminUrlKeys } from '../../shared-module/common/constants/admin-url-key.constants';
import { CpqToastrService } from '../../shared-module/cpq-toastr/cpq-toastr.service';

@Component({
    selector: 'menu-nav-bar',
    templateUrl: './menu-nav-bar.component.html',
    styleUrls: ['./menu-nav-bar.component.scss'],
    standalone: false
})
export class MenuNavBarComponent implements OnInit {
  public menies: Array<MenuParentNode>;
  public isTranslated = false;
  public isHiddenInit = false;
  public changeSetOptions: Array<ILookup<number, string>> = new Array<ILookup<number, string>>();
  public SelectType = SelectType;
  public selectedChangeSet: ILookup<number, string>;
  public IconType = IconType;
  public displayChangeSetDropdown: boolean;
  public createNewItemOptions: CreateNewItemOptions;
  public MenuInfoLinks = MenuInfoLinks;
  private displayChangeSetDropdownObserver = new BehaviorSubject<any>(null);
  @ViewChild(CpqSelectComponent) select: CpqSelectComponent;

  @HostListener('document:click', ['$event']) onClick(event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.displayChangeSetDropdown = false;
    }
  }

  constructor(private router: Router,
    private elementRef: ElementRef,
    public menuService: MenuService,
    private changeSetService: ChangeSetService,
    public translateService: TranslationService,
    public tenantSettings: TenantSettings,
    public user: CurrentUser,
    private dialog: MatDialog,
    private toastr: CpqToastrService,
    private changeDetector: ChangeDetectorRef,
    private translationService: TranslationService) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setActive();
      }
    });
  }

  ngOnInit() {
    this.init();
    this.menuService.refreshMenuNodes.subscribe((res) => {
      if(res) {
        this.init();
      }
    });
  }

  init() {
    this.menies = new Array<MenuParentNode>();

    this.changeSetService.getChangeSetLookups().subscribe(changeSetOptions => {
      this.changeSetOptions = changeSetOptions;
      this.changeSetService.getSelectedChangeSet().subscribe(changeSet => this.selectedChangeSet = changeSet);
    });

    this.translateService.translateMenu().subscribe(translations => {
      for (const menu of MENIES) {
        let value = translations.find(x => x.name === menu.key);
        if (value) {
          menu.label = value.value;
        }
        if (menu.children) {
          for (const child of menu.children) {
            if (child.id === AdminUrlKeys.DISCOUNTS) {
              child.key = this.tenantSettings.useNewQuoteEngine ? 'xmlDiscounts' : 'xmlDiscountsMultipliers';
            }
            if (child.id === AdminUrlKeys.CUSTOMER_ROLES_MAPPING) {
              child.key = this.tenantSettings.isBusinessPartnersEnabled ? 'xmlPartnerFunctionsMapping' : 'xmlCustomerRolesMapping';
            }
            value = translations.find(x => x.name === child.key);
            if (value) {
              child.label = value.value;
            }
          }
        }
        this.menies.push(menu);
        this.isTranslated = true;
      }
    });

    this.menuService.getHiddenMenuItemKeys().subscribe(hiddenMenuKeys => {
      MENIES.forEach(menuParentNode => {
        menuParentNode.isVisible = !hiddenMenuKeys.includes(menuParentNode.id);

        if (menuParentNode.isVisible && menuParentNode.children) {
          menuParentNode.children.forEach(menuChildNode => {
            menuChildNode.isVisible = !hiddenMenuKeys.includes(menuChildNode.id);

            if (menuChildNode.activeNodeId) {
              menuChildNode.isVisible = false;
            }
          });

          if (menuParentNode.children && menuParentNode.children.every(x => !x.isVisible)) {
            menuParentNode.isVisible = false;
          }
        }
      });
      this.isHiddenInit = true;
    });

    this.changeSetService.changeSetModified.subscribe(() => {
      this.changeSetService.getChangeSetLookups().subscribe(changeSets => {
        this.changeSetOptions = changeSets;
      });
    });

    this.displayChangeSetDropdownObserver.pipe(debounceTime(0)).subscribe((displayChangeSetDropdown: boolean) => {
      if (displayChangeSetDropdown) {
        this.select.suggest();
        this.select.toggleActionMenu();
      }
    });

    this.createNewItemOptions = {
      visible: this.tenantSettings.canAddEditChangeSet,
      callback: () => this.openNewChangeSetModal()
    };
  }

  setActive() {
    let url = this.router.url;
    // remove the slash at the begining of url because menu links don't start with a slash
    if (url.indexOf('/') === 0) {
      url = url.substring(1, url.length);
    }

    // find all slashes and note their positions
    const slashPositions = [];
    for (let i = 0; i < url.length; i++) {
      if (url[i] === '/') { slashPositions.push(i); }
    }

    // remove all characters in url from the second slash onwards
    // e.g. 'users/user-types/add-edit-user-type/1' will be shortened to 'users/user-types'
    // so that it will match the corresponding menu link
    if (slashPositions.length > 1) {
      url = url.substring(0, slashPositions[1]);
    }

    //remove query params from url
    // e.g. import-export/translations?refineFilters=true -> import-export/translations
    if(url.indexOf('?') > -1){
      url = url.substring(0, url.indexOf('?'))
    }

    MENIES.forEach(menu => {
      if (menu.children) {
        menu.isCollapsed = true;
        menu.children.forEach(child => {
          if (child.link === url) {
            // e.g. 'users'
            this.setActiveLink(menu, child);
          } else if (slashPositions.length > 1 && child.link.startsWith(url)) {
            // e.g. 'users/user-types' or 'users/user-types/add-edit-user-type/0'
            this.setActiveLink(menu, child);
          } else if (slashPositions.length === 2 && child.link === url.substring(0, slashPositions[0])) {
            // e.g. 'users/add-edit-user/0'
           this.setActiveLink(menu, child);
          } else {
            child.active = false;
          }
        });
      } else if (menu.link === url) {
        menu.active = true;
      } else {
        menu.active = false;
      }
    });
  }

  private setActiveLink(parent: MenuParentNode, child: MenuNode){
    child.active = true;
    parent.isCollapsed = false;
    if(child.activeNodeId){
      if(!any(parent.children, x => x.id == child.activeNodeId)) {
        throw new Error("Side menu link is missing. Make sure you set activeNodeId to the correct value");
      }
      parent.children.find(x => x.id === child.activeNodeId).active = true;
      child.active = false;
    }
  }

  public openNewChangeSetModal() {
    this.dialog.open(CpqAddNewChangeSetDialogComponent, {
      data: new ChangeSet()
    })
      .afterClosed()
      .subscribe(newChangeSet => {
        if (newChangeSet) {
          this.changeSetService.save(newChangeSet).subscribe(id => {
            this.menuService.newChangeSet.next(true);
            this.toastr.success(this.translationService.translate('Actions.GenericAction.SaveSuccess'));
            this.changeSetService.getChangeSetLookups().subscribe(changeSets => {
              this.changeSetOptions = changeSets;
              this.selectedChangeSet.id = id;
              this.selectChangeSet();
            });
          });
        }
      });
  }

  public selectChangeSet() {
    if (this.selectedChangeSet && this.selectedChangeSet.id) {
      this.changeSetService.selectChangeSet(this.selectedChangeSet.id).subscribe(() => {
        this.selectedChangeSet.text = this.changeSetOptions.find(x => x.id === this.selectedChangeSet.id).text;
      });
    }
  }

  public selectPreviousChangeSetIfEmpty() {
    if (!this.selectedChangeSet.id) {
      this.changeSetService.getSelectedChangeSet().subscribe(selectedChangeSet => this.selectedChangeSet = selectedChangeSet);
    }
  }

  public onInfoClick(infoLink: MenuInfoLinks) {
    let link = '';
    switch(infoLink) {
      case MenuInfoLinks.ChangeSet:
        link = 'https://help.sap.com/viewer/884885f05e6b4c8082254d4d9d63f19b/latest/en-US/a024c4a52c11439f80a1417d033e4309.html';
        break;
      case MenuInfoLinks.TenantUrl:
        link = 'https://help.sap.com/viewer/884885f05e6b4c8082254d4d9d63f19b/latest/en-US/09e46842d4b94c13934323651b81b395.html'
    }
    window.open(link);
  }

  public toggleDisplayChangeSetDropdown() {
    this.displayChangeSetDropdown = !this.displayChangeSetDropdown;
    this.displayChangeSetDropdownObserver.next(this.displayChangeSetDropdown);
    this.changeDetector.detectChanges();
  }
}
